import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';
import { resolveUserStatus, shouldSyncVerifiedUserStatus, mapUserAccountFields } from '../utils/authState';
import { useClaimSync } from '../hooks/useClaimSync';
import { consumeIntentionalLogout, markSessionExpired } from '../utils/authSessionFlags';
import { isPreAuthRenderPath } from '../config/preAuthBootRoutes';

// NOTE: Branded verification email is now sent automatically by the onAuthCreateBasic
// server-side trigger (ADR-077 fix, 2026-01-27). No client-side email function needed.

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userEntitlements, setUserEntitlements] = useState([]);
  const [userAccount, setUserAccount] = useState({});
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Captured ONCE at boot: public marketing routes render before Firebase Auth
  // resolves (see preAuthBootRoutes.js). Never re-evaluated, so children never
  // unmount mid-session — after the first onAuthStateChanged tick `loading` is
  // false forever and SPA navigation behaves exactly as before this flag existed.
  // (AuthProvider mounts outside the Router, hence window.location, guarded for
  // non-browser test environments.)
  const [preAuthRender] = useState(() => (
    typeof window !== 'undefined' && isPreAuthRenderPath(window.location.pathname)
  ));
  const auth = getAuth();
  const db = getFirestore();
  // Tracks whether a user was signed in on the previous auth-state tick, so an
  // unexpected drop to null (token/refresh failure, admin suspend) can be told
  // apart from an intentional sign-out.
  const hadUserRef = useRef(false);
  // Holds the unsubscribe for the live users/{uid} listener so it can be torn
  // down on sign-out, auth-user swap, and effect cleanup.
  const accountUnsubRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        hadUserRef.current = true;
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        // NOTE: Do NOT call getIdToken(true) here.
        // onAuthStateChanged already provides a valid user with a fresh token.
        // Force-refreshing creates race conditions with other token consumers
        // (auto-save, cloud functions), especially right after email verification.
        // Claims refresh happens lazily via syncAuthStatus below when needed.

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const resolvedStatus = resolveUserStatus(user, userData);

          // Set new architecture fields
          setUserStatus(resolvedStatus);
          setUserRoles(userData.roles || []);
          setUserEntitlements(userData.entitlements || []);
          setUserAccount(mapUserAccountFields(userData));

          // Check if user is verified but status hasn't been upgraded yet
          if (shouldSyncVerifiedUserStatus(user, userData)) {
            try {
              console.log('User email verified but status not upgraded - calling syncAuthStatus');
              const syncAuthStatus = httpsCallable(functions, 'syncAuthStatus');
              const result = await syncAuthStatus();

              if (result.data.success) {
                console.log('Status upgraded successfully:', result.data);
                // Update local state with new status
                setUserStatus('active');
              } else {
                console.log('Status sync not needed:', result.data.message);
              }
            } catch (error) {
              console.error('Error syncing auth status:', error);
            }
          }

          setCurrentUser(user);
        } else {
          setCurrentUser(user);
          setUserStatus(null);
          setUserRoles([]);
          setUserEntitlements([]);
          setUserAccount({});
        }

        // Check if we should claim assessments (only for users who haven't been checked yet)
        if (userDoc.exists() && !userDoc.data().hasClaimedAssessments && !userDoc.data().lastClaimCheck) {
          // Delay claim attempt to ensure auth token is properly set
          setTimeout(async () => {
            try {
              // Use cached token — avoid forced refresh to prevent token stampede
              const idToken = await user.getIdToken();

              const claimAssessments = httpsCallable(functions, 'claimAssessments');
              const result = await claimAssessments({});

              if (result.data.success && result.data.claimed > 0) {
                console.log(`Claimed ${result.data.claimed} assessments for user`);
                // Mark that we've checked
                await updateDoc(userDocRef, {
                  lastClaimCheck: new Date()
                });
              }
            } catch (error) {
              console.error('Failed to claim assessments:', error);
              // Mark that we tried so we don't keep retrying
              await updateDoc(userDocRef, {
                lastClaimCheck: new Date()
              });
            }
          }, 2000); // Wait 2 seconds for auth to stabilize
        }

        // Credits and entitlements change mid-session (spends, grants, plan
        // changes); the login-time getDoc snapshot above goes stale as soon as
        // that happens. Attach a live listener so every useAuth consumer sees
        // the current balance/entitlements instead of the login-time values
        // (GH #696 round-2: Overview showed 400/400 while live balance was 255).
        if (accountUnsubRef.current) accountUnsubRef.current();
        accountUnsubRef.current = onSnapshot(userDocRef, (snap) => {
          if (!snap.exists()) return;
          const liveData = snap.data();
          setUserStatus(resolveUserStatus(user, liveData));
          setUserRoles(liveData.roles || []);
          setUserEntitlements(liveData.entitlements || []);
          setUserAccount(mapUserAccountFields(liveData));
        }, (err) => {
          console.warn('AuthContext: users doc listener error:', err.message);
        });
      } else {
        // Distinguish an intentional sign-out from an unexpected auth drop
        // (e.g. the ~60-min refresh failing on a revoked/expired token). If a
        // user was signed in and this was NOT a deliberate logout, flag the
        // session as expired so PrivateRoute can route to /login with a notice.
        const wasSignedIn = hadUserRef.current;
        const intentional = consumeIntentionalLogout();
        if (wasSignedIn && !intentional) {
          markSessionExpired();
        }
        hadUserRef.current = false;

        // Tear down the live account listener when the user signs out.
        if (accountUnsubRef.current) {
          accountUnsubRef.current();
          accountUnsubRef.current = null;
        }

        setCurrentUser(null);
        setUserStatus(null);
        setUserRoles([]);
        setUserEntitlements([]);
        setUserAccount({});
      }
      setLoading(false);
      setAuthReady(true); // Auth state has been determined
      setIsLoading(false); // Set isLoading to false at the very end
    });

    return () => {
      if (accountUnsubRef.current) {
        accountUnsubRef.current();
        accountUnsubRef.current = null;
      }
      unsubscribe();
    };
  }, [auth, db]);

  // Force-refresh the ID token when the entitlements doc mirror drifts from the
  // token's claims (replaces backend refresh-token revocation — the buyer-logout
  // P1 fix). Keeps paid features unlocking within seconds of purchase without
  // killing the session.
  useClaimSync(currentUser);

  const signIn = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email, password, fullName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // The auth trigger (onAuthCreate) creates the user document with proper status/roles/entitlements
    // We only add profile data that the trigger doesn't know about
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      fullName: fullName || ''
    }, { merge: true });

    return userCredential;
  };

  // Helper functions for status/entitlements architecture
  const hasEntitlement = (entitlement) => {
    return userEntitlements.includes(entitlement);
  };

  const hasRole = (role) => {
    return userRoles.includes(role);
  };

  const isAdmin = () => {
    return userRoles.includes('admin');
  };

  // Memoize so all useAuth consumers only re-render when actual auth data changes,
  // not on every AuthContext render (which would recreate inline function refs).
  const value = useMemo(() => ({
    currentUser,
    userStatus,
    userRoles,
    userEntitlements,
    userAccount,
    signIn,
    signUp,
    authReady,
    isLoading,
    hasEntitlement,
    hasRole,
    isAdmin,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [currentUser, userStatus, userRoles, userEntitlements, userAccount, authReady, isLoading]);

  return (
    <AuthContext.Provider value={value}>
      {(preAuthRender || !loading) && children}
    </AuthContext.Provider>
  );
};
